import axios from "axios";
import React, { createContext, useState, useContext, useEffect } from "react";
import { baseUrl } from "./config";

const AuthContext = createContext();

function extractRoles(payload) {
  if (!payload || typeof payload !== "object") return [];
  const direct = payload.roles;
  const nested = payload.user?.roles;
  if (Array.isArray(direct)) return direct;
  if (Array.isArray(nested)) return nested;
  return [];
}

function extractPseudo(payload) {
  if (!payload || typeof payload !== "object") return null;
  return payload.pseudo ?? payload.user?.pseudo ?? null;
}

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [pseudo, setPseudo] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const login = userPseudo => {
    setIsLoggedIn(true);
    setPseudo(userPseudo);
  };

  const logout = () => {
    setIsLoggedIn(false);
    setPseudo(null);
    setRoles([]);
  };

  // For when the user refresh, check if there is a session
  useEffect(() => {
    const applyAuthenticatedPayload = payload => {
      setIsLoggedIn(true);
      setPseudo(extractPseudo(payload));
      setRoles(extractRoles(payload));
    };

    const bootstrapAuth = async () => {
      try {
        // `/me/profile` contains canonical roles used for create permissions.
        const profileRes = await axios.get(baseUrl + "/me/profile", {
          withCredentials: true,
        });
        applyAuthenticatedPayload(profileRes.data);
      } catch {
        try {
          // Fallback for older backends where only `/me` exists.
          const meRes = await axios.get(baseUrl + "/me", { withCredentials: true });
          const nextPseudo = extractPseudo(meRes.data);
          const nextRoles = extractRoles(meRes.data);
          const authenticated = !!nextPseudo || nextRoles.length > 0 || !!meRes.data?.user;

          if (authenticated) {
            applyAuthenticatedPayload(meRes.data);
            return;
          }
        } catch {
          // Ignore fallback error and treat as logged out below.
        }
        setIsLoggedIn(false);
        setPseudo(null);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    bootstrapAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        pseudo,
        roles,
        login,
        logout,
        setIsLoggedIn,
        setPseudo,
        setRoles,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
